import type { JobDto, MeResponse } from '@/lib/types';

/**
 * `{{variables}}` in message text. Mirrors `templates.ts` in @crewline/shared, which the API uses to
 * report a template's variables and to fill them when a text goes out.
 *
 * - built-in (`job.name`, `date`, `sender.firstName`): filled from the job, the clock and you;
 * - per recipient (`contact.firstName`, `contact.company`): filled at send time, one text per person;
 * - custom (`gate`, `reason`): blanks asked for when a template is used, never sent as-is.
 *
 * `{{contact.firstName|there}}` names a fallback for contacts without that field.
 */

export type TemplateVariableKind = 'builtin' | 'contact' | 'custom';

export interface TemplateVariable {
  key: string;
  builtIn: boolean;
  kind: TemplateVariableKind;
  fallback?: string;
}

export interface VariableInfo {
  key: string;
  label: string;
  example: string;
}

export const BUILTIN_VARIABLES: ReadonlyArray<VariableInfo> = [
  { key: 'job.name', label: 'Job name', example: 'Orlando Tower' },
  { key: 'job.number', label: 'Job phone number', example: '(407) 555-0142' },
  { key: 'job.address', label: 'Site address', example: '400 W Church St, Orlando' },
  { key: 'date', label: "Today's date", example: 'Sep 23' },
  { key: 'time', label: 'Current time', example: '6:42 AM' },
  { key: 'sender.name', label: 'Your name', example: 'Danielle Ortiz' },
  { key: 'sender.firstName', label: 'Your first name', example: 'Danielle' },
];

export const CONTACT_VARIABLES: ReadonlyArray<VariableInfo> = [
  { key: 'contact.firstName', label: 'First name', example: 'Terrance' },
  { key: 'contact.lastName', label: 'Last name', example: 'Cole' },
  { key: 'contact.name', label: 'Full name', example: 'Terrance Cole' },
  { key: 'contact.company', label: 'Company', example: 'Cole Electric' },
  { key: 'contact.title', label: 'Title', example: 'Foreman' },
];

export const KIND_LABEL: Record<TemplateVariableKind, string> = {
  builtin: 'Filled automatically',
  contact: 'Personalized per recipient',
  custom: 'Asked when used',
};

const TOKEN = /\{\{\s*([A-Za-z][A-Za-z0-9_.]*)\s*(?:\|\s*([^{}|]*?)\s*)?\}\}/g;

export function variableKind(key: string): TemplateVariableKind {
  if (BUILTIN_VARIABLES.some((v) => v.key === key)) return 'builtin';
  if (CONTACT_VARIABLES.some((v) => v.key === key)) return 'contact';
  return 'custom';
}

export function extractTemplateVariables(body: string): TemplateVariable[] {
  const seen = new Set<string>();
  const out: TemplateVariable[] = [];
  for (const m of body.matchAll(TOKEN)) {
    const key = m[1]!;
    if (seen.has(key)) continue;
    seen.add(key);
    const kind = variableKind(key);
    const fallback = m[2]?.trim();
    out.push({ key, builtIn: kind !== 'custom', kind, ...(fallback ? { fallback } : {}) });
  }
  return out;
}

export function hasTemplateTokens(text: string): boolean {
  return new RegExp(TOKEN.source).test(text);
}

/**
 * Fill tokens from `values`; empty values fall back to the token's `|fallback`, or to nothing.
 * `keep` leaves chosen tokens in place (per-recipient ones, filled later by the API).
 */
export function renderTemplate(body: string, values: Record<string, string>, opts: { keep?: (key: string) => boolean } = {}): string {
  return body.replace(TOKEN, (m, key: string, fallback: string | undefined) => {
    if (opts.keep?.(key)) return m;
    const v = values[key];
    return v && v.trim() ? v : (fallback ?? '');
  });
}

export const isContactVariable = (key: string): boolean => variableKind(key) === 'contact';

/** Values for the built-in variables from the job you are composing for and the signed-in person. */
export function builtinValues(job: Pick<JobDto, 'name' | 'address' | 'phoneNumber'>, me: MeResponse): Record<string, string> {
  const now = new Date();
  return {
    'job.name': job.name,
    'job.number': job.phoneNumber?.display ?? '',
    'job.address': job.address ?? '',
    date: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(now),
    time: new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(now),
    'sender.name': me.user.fullName,
    'sender.firstName': me.user.firstName || me.user.fullName,
  };
}

/** Sample recipient values for previews before a real audience is known. */
export function contactExampleValues(): Record<string, string> {
  return Object.fromEntries(CONTACT_VARIABLES.map((v) => [v.key, v.example]));
}

/** Sample values for every variable: real-looking examples for the ones the app fills, `[key]` for blanks. */
export function exampleValues(vars: TemplateVariable[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of vars) out[v.key] = [...BUILTIN_VARIABLES, ...CONTACT_VARIABLES].find((b) => b.key === v.key)?.example ?? `[${v.key}]`;
  return out;
}

/** "contact.firstName" → "First name"; "start_time" → "Start time". */
export function variableLabel(key: string): string {
  const known = [...BUILTIN_VARIABLES, ...CONTACT_VARIABLES].find((v) => v.key === key);
  if (known) return known.label;
  const s = key
    .replace(/[_.]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Split text into literal runs and tokens, for rendering tokens as chips. */
export function tokenize(body: string): Array<{ type: 'text'; value: string } | { type: 'token'; key: string; kind: TemplateVariableKind; fallback?: string; raw: string }> {
  const out: Array<{ type: 'text'; value: string } | { type: 'token'; key: string; kind: TemplateVariableKind; fallback?: string; raw: string }> = [];
  let last = 0;
  for (const m of body.matchAll(TOKEN)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ type: 'text', value: body.slice(last, i) });
    const key = m[1]!;
    const fallback = m[2]?.trim();
    out.push({ type: 'token', key, kind: variableKind(key), raw: m[0], ...(fallback ? { fallback } : {}) });
    last = i + m[0].length;
  }
  if (last < body.length) out.push({ type: 'text', value: body.slice(last) });
  return out;
}
